import { useHandler } from "@redotech/react-util/hook";
import PlayIcon from "@redotech/redo-icons/play_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-01_filled.svg";
import { memo, useState } from "react";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "./arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import type {
  ExtensionArtifactClient,
  ExtensionArtifactTestInputLoader,
} from "./extension-artifact-code-editor-modal";
import {
  ContentSearchControl,
  type ExtensionArtifactTestRunState,
  SearchableSectionHeader,
  TestRunOutput,
} from "./extension-artifact-test-pane-components";
import { Flex } from "./flex";
import { JsonValueEditor, jsonValueMatchesSearch } from "./json-value-editor";
import { Text } from "./text";
import { useExtensionArtifactTestInput } from "./use-extension-artifact-test-input";

/** Editable test input and execution output for one extension artifact. */
export const ExtensionArtifactTestPane = memo(
  function ExtensionArtifactTestPane({
    client,
    contractId,
    inputSelector,
    code,
    sampleInput,
    inputJsonSchema,
    testInputLoader,
  }: {
    client: ExtensionArtifactClient;
    contractId: string;
    inputSelector: string[];
    code: string;
    sampleInput: unknown;
    /** JSON Schema for the pruned input — types fields whose sample is null. */
    inputJsonSchema?: unknown;
    testInputLoader?: ExtensionArtifactTestInputLoader;
  }) {
    // The editable input re-seeds whenever the sample changes (contract or
    // selector switch), since the old shape no longer matches the contract.
    const [seededFrom, setSeededFrom] = useState(sampleInput);
    const [input, setInput] = useState<unknown>(() =>
      structuredClone(sampleInput),
    );
    if (seededFrom !== sampleInput) {
      setSeededFrom(sampleInput);
      setInput(structuredClone(sampleInput));
    }

    const [run, setRun] = useState<ExtensionArtifactTestRunState>({
      status: "idle",
    });
    const [inputSearchString, setInputSearchString] = useState("");
    const [outputSearchString, setOutputSearchString] = useState("");
    const [testInputKey, setTestInputKey] = useState("");
    const inputLoad = useExtensionArtifactTestInput({
      loader: testInputLoader,
      onLoaded: (loadedInput) => {
        setInput(loadedInput);
        setRun({ status: "idle" });
      },
    });
    const handleLoadInput = useHandler(() => {
      const trimmedInputKey = testInputKey.trim();
      if (!trimmedInputKey) {
        return;
      }
      inputLoad.mutate(trimmedInputKey);
    });

    const handleRun = useHandler(async () => {
      setRun({ status: "running" });
      try {
        const result = await client.testExtensionArtifact({
          contractId,
          code,
          inputSelector,
          input,
        });
        setRun({ status: "done", result });
      } catch (err) {
        setRun({
          status: "failed",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    });

    const handleReset = useHandler(() => {
      setInput(structuredClone(sampleInput));
    });

    return (
      <Flex dir="column" gap="lg">
        {testInputLoader ? (
          <Flex dir="column" gap="xs">
            <Flex align="flex-end" gap="sm">
              <Flex grow={1}>
                <RedoTextInput
                  fullWidth
                  label={testInputLoader.label}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void handleLoadInput();
                    }
                  }}
                  placeholder={testInputLoader.placeholder}
                  setValue={setTestInputKey}
                  size="sm"
                  value={testInputKey}
                />
              </Flex>
              <RedoButton
                disabled={!testInputKey.trim() || inputLoad.isPending}
                hierarchy="secondary"
                onClick={handleLoadInput}
                pending={inputLoad.isPending}
                size="sm"
                text="Load"
              />
            </Flex>
            {inputLoad.error && !inputLoad.isPending ? (
              <Text fontSize="xs" textColor="error">
                {inputLoad.error instanceof Error
                  ? inputLoad.error.message
                  : String(inputLoad.error)}
              </Text>
            ) : null}
          </Flex>
        ) : null}
        <Flex align="center" gap="sm" justify="space-between">
          <Text fontSize="xs" textColor="secondary">
            Run your code against the input below.
          </Text>
          <Flex align="center" gap="sm" shrink={0}>
            <RedoButton
              hierarchy="tertiary"
              IconLeading={RefreshIcon}
              onClick={handleReset}
              size="xs"
              text="Reset"
            />
            <RedoButton
              disabled={run.status === "running"}
              hierarchy="primary"
              IconLeading={PlayIcon}
              onClick={handleRun}
              pending={run.status === "running"}
              size="xs"
              text={run.status === "running" ? "Running…" : "Run"}
            />
          </Flex>
        </Flex>

        <RedoCollapseSubsection
          defaultCollapsed={false}
          renderHeader={({ leadingIcon, onClick }) => (
            <SearchableSectionHeader
              leadingIcon={leadingIcon}
              onClick={onClick}
              title="Input"
            >
              <ContentSearchControl
                placeholder="Search input..."
                searchString={inputSearchString}
                setSearchString={setInputSearchString}
              />
            </SearchableSectionHeader>
          )}
          title="Input"
        >
          <Flex dir="column" gap="xs" pl="xl" pt="xs">
            <Flex dir="column" gap="xs" w="full">
              {typeof input === "object" && input !== null ? (
                !inputSearchString.trim() ||
                jsonValueMatchesSearch(input, inputSearchString) ? (
                  <JsonValueEditor
                    defaultCollapsed={false}
                    label="input"
                    schema={inputJsonSchema}
                    searchLabel={false}
                    searchString={inputSearchString}
                    setValue={setInput}
                    value={input}
                  />
                ) : (
                  <Text fontSize="xs" textColor="secondary">
                    No matching fields
                  </Text>
                )
              ) : (
                <Text fontSize="xs" textColor="secondary">
                  This contract takes no input fields
                  {inputSelector.length === 0
                    ? " — select input fields to test against them"
                    : ""}
                  .
                </Text>
              )}
            </Flex>
          </Flex>
        </RedoCollapseSubsection>

        <RedoCollapseSubsection
          defaultCollapsed={false}
          renderHeader={({ leadingIcon, onClick }) => (
            <SearchableSectionHeader
              leadingIcon={leadingIcon}
              onClick={onClick}
              title="Output"
            >
              <ContentSearchControl
                placeholder="Search output..."
                searchString={outputSearchString}
                setSearchString={setOutputSearchString}
              />
            </SearchableSectionHeader>
          )}
          title="Output"
        >
          <Flex dir="column" gap="lg" pl="xl" pt="xs">
            <TestRunOutput run={run} searchString={outputSearchString} />
          </Flex>
        </RedoCollapseSubsection>
      </Flex>
    );
  },
);
